import React from 'react';
import {
  CheckCircle, AlertTriangle, XCircle,
  Video, Signal, HardDrive, Cloud, Play, Pause,
  MapPin, Battery, Navigation,
} from 'lucide-react';

const DeviceDetailPanel = ({
  selectedDevice,
  monitoringData,
  alerts,
  categoryData,
  onCloseDetail,
  formatBytes,
}) => {
  if (selectedDevice && monitoringData) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Device Details</h3>
            <button onClick={onCloseDetail} className="text-gray-400 hover:text-gray-600">
              <XCircle className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Status</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                monitoringData.status === 'online' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {monitoringData.status}
              </span>
            </div>

            {/* Scores */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{monitoringData.health_score}</p>
                <p className="text-xs text-blue-600">Health Score</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-600">{monitoringData.security_score}</p>
                <p className="text-xs text-purple-600">Security Score</p>
              </div>
            </div>

            {/* System Resources */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">System Resources</p>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>CPU</span>
                    <span>{monitoringData.cpu_usage_percent}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${monitoringData.cpu_usage_percent}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Memory</span>
                    <span>{monitoringData.memory_usage_percent}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: `${monitoringData.memory_usage_percent}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Network Stats */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Network (24h)</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500">&#8595; Download</p>
                  <p className="font-medium">{formatBytes(monitoringData.network?.bytes_in_24h || 0)}</p>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500">&#8593; Upload</p>
                  <p className="font-medium">{formatBytes(monitoringData.network?.bytes_out_24h || 0)}</p>
                </div>
              </div>
            </div>

            {/* Camera Specific */}
            {monitoringData.camera_specific && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Camera Status</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    {monitoringData.camera_specific.recording_status === 'recording' ? (
                      <Play className="h-4 w-4 text-red-500" />
                    ) : (
                      <Pause className="h-4 w-4 text-gray-400" />
                    )}
                    <span>{monitoringData.camera_specific.recording_status}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-gray-500" />
                    <span>{monitoringData.camera_specific.fps} FPS</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Signal className="h-4 w-4 text-gray-500" />
                    <span>{monitoringData.camera_specific.bitrate_kbps} kbps</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <HardDrive className="h-4 w-4 text-gray-500" />
                    <span>{monitoringData.camera_specific.storage_days_remaining}d storage</span>
                  </div>
                </div>
              </div>
            )}

            {/* Meter Specific */}
            {monitoringData.meter_specific && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Meter Status</p>
                <div className="p-3 bg-yellow-50 rounded-lg text-center mb-2">
                  <p className="text-3xl font-bold text-yellow-700">
                    {monitoringData.meter_specific.current_reading}
                  </p>
                  <p className="text-sm text-yellow-600">{monitoringData.meter_specific.reading_unit}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Signal className="h-4 w-4 text-gray-500" />
                    <span>{monitoringData.meter_specific.signal_strength_dbm} dBm</span>
                  </div>
                  {monitoringData.meter_specific.battery_level_percent && (
                    <div className="flex items-center gap-2">
                      <Battery className="h-4 w-4 text-gray-500" />
                      <span>{monitoringData.meter_specific.battery_level_percent}%</span>
                    </div>
                  )}
                </div>
                {monitoringData.meter_specific.tamper_detected && (
                  <div className="mt-2 p-2 bg-red-100 rounded text-red-700 text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Tamper detected!
                  </div>
                )}
              </div>
            )}

            {/* Dashcam Specific */}
            {monitoringData.dashcam_specific && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Dashcam Status</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    {monitoringData.dashcam_specific.recording_status === 'recording' ? (
                      <Play className="h-4 w-4 text-red-500" />
                    ) : (
                      <Pause className="h-4 w-4 text-gray-400" />
                    )}
                    <span>{monitoringData.dashcam_specific.recording_status}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <HardDrive className="h-4 w-4 text-gray-500" />
                    <span>{monitoringData.dashcam_specific.storage_used_percent}% used</span>
                  </div>
                  {monitoringData.dashcam_specific.vehicle_speed_kmh !== null && (
                    <div className="flex items-center gap-2">
                      <Navigation className="h-4 w-4 text-gray-500" />
                      <span>{monitoringData.dashcam_specific.vehicle_speed_kmh} km/h</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Cloud className="h-4 w-4 text-gray-500" />
                    <span>{monitoringData.dashcam_specific.cloud_sync_status}</span>
                  </div>
                </div>
                {monitoringData.dashcam_specific.gps_location && (
                  <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                    <div className="flex items-center gap-2 text-green-700">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {monitoringData.dashcam_specific.gps_location.lat.toFixed(4)},{' '}
                        {monitoringData.dashcam_specific.gps_location.lng.toFixed(4)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Category Stats below detail */}
        <CategoryStats categoryData={categoryData} />
      </div>
    );
  }

  // No device selected — show alerts + category stats
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border shadow-sm p-4">
        <h3 className="font-semibold text-gray-900 mb-4">Active Alerts</h3>
        {alerts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-300" />
            <p>No active alerts</p>
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.slice(0, 5).map((alert) => (
              <div key={alert.id} className={`p-3 rounded-lg border-l-4 ${
                alert.severity === 'critical' ? 'bg-red-50 border-red-500' :
                alert.severity === 'high' ? 'bg-orange-50 border-orange-500' :
                alert.severity === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                'bg-blue-50 border-blue-500'
              }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm">{alert.alert_type?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-600">{alert.details}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    alert.severity === 'critical' ? 'bg-red-200 text-red-700' :
                    alert.severity === 'high' ? 'bg-orange-200 text-orange-700' :
                    'bg-yellow-200 text-yellow-700'
                  }`}>
                    {alert.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CategoryStats categoryData={categoryData} />
    </div>
  );
};

const CategoryStats = ({ categoryData }) => {
  if (!categoryData) return null;
  return (
    <div className="bg-white rounded-xl border shadow-sm p-4">
      <h3 className="font-semibold text-gray-900 mb-4">Category Statistics</h3>
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Online</span>
          <span className="font-medium text-green-600">{categoryData.statistics?.online || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Offline</span>
          <span className="font-medium text-red-600">{categoryData.statistics?.offline || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Avg Health</span>
          <span className="font-medium">{categoryData.statistics?.average_health_score || 100}%</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Total Alerts</span>
          <span className="font-medium text-orange-600">{categoryData.statistics?.total_alerts || 0}</span>
        </div>
      </div>
    </div>
  );
};

export default DeviceDetailPanel;
